import { Clock, Users, FolderKanban, Calendar } from 'lucide-react'

import type { TimelogStatistics } from '@/hooks'

interface TimelogStatsProps {
  stats: TimelogStatistics
}

/**
 * 工时统计组件
 */
export function TimelogStats({ stats }: TimelogStatsProps) {
  return (
    <div className="space-y-6 rounded-lg border bg-card p-6">
      {/* 总览 */}
      <div className="flex items-center gap-3">
        <div className="rounded-full bg-primary/10 p-3">
          <Clock className="h-6 w-6 text-primary" />
        </div>
        <div>
          <div className="text-sm text-muted-foreground">总工时</div>
          <div className="text-3xl font-bold">{stats.totalHours.toFixed(1)} 小时</div>
          <div className="text-sm text-muted-foreground">
            ({stats.totalMinutes} 分钟)
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* 按项目统计 */}
        <div>
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <FolderKanban className="h-4 w-4" />
            按项目
          </div>
          {stats.byProject.length === 0 ? (
            <div className="text-sm text-muted-foreground">暂无数据</div>
          ) : (
            <div className="space-y-2">
              {stats.byProject.slice(0, 5).map((item) => (
                <div key={item.projectId} className="flex items-center justify-between">
                  <span className="text-sm truncate max-w-[150px]" title={item.projectName}>
                    {item.projectName}
                  </span>
                  <span className="text-sm font-medium">{item.hours.toFixed(1)}h</span>
                </div>
              ))}
              {stats.byProject.length > 5 && (
                <div className="text-xs text-muted-foreground">
                  还有 {stats.byProject.length - 5} 个项目...
                </div>
              )}
            </div>
          )}
        </div>

        {/* 按员工统计 */}
        <div>
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Users className="h-4 w-4" />
            按员工
          </div>
          {stats.byEmployee.length === 0 ? (
            <div className="text-sm text-muted-foreground">暂无数据</div>
          ) : (
            <div className="space-y-2">
              {stats.byEmployee.slice(0, 5).map((item) => (
                <div key={item.employeeId} className="flex items-center justify-between">
                  <span className="text-sm truncate max-w-[150px]" title={item.employeeName}>
                    {item.employeeName}
                  </span>
                  <span className="text-sm font-medium">{item.hours.toFixed(1)}h</span>
                </div>
              ))}
              {stats.byEmployee.length > 5 && (
                <div className="text-xs text-muted-foreground">
                  还有 {stats.byEmployee.length - 5} 个员工...
                </div>
              )}
            </div>
          )}
        </div>

        {/* 按日期统计 */}
        <div>
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Calendar className="h-4 w-4" />
            按日期
          </div>
          {stats.byDate.length === 0 ? (
            <div className="text-sm text-muted-foreground">暂无数据</div>
          ) : (
            <div className="space-y-2">
              {stats.byDate.slice(0, 5).map((item) => (
                <div key={item.date} className="flex items-center justify-between">
                  <span className="text-sm">
                    {new Date(item.date).toLocaleDateString('zh-CN', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                  <span className="text-sm font-medium">{item.hours.toFixed(1)}h</span>
                </div>
              ))}
              {stats.byDate.length > 5 && (
                <div className="text-xs text-muted-foreground">
                  还有 {stats.byDate.length - 5} 天...
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 简单的条形图 */}
      {stats.byProject.length > 0 && (
        <div>
          <div className="mb-3 text-sm font-medium">项目工时分布</div>
          <div className="space-y-2">
            {stats.byProject.slice(0, 5).map((item) => {
              const percentage = stats.totalHours > 0 
                ? (item.hours / stats.totalHours) * 100 
                : 0
              return (
                <div key={item.projectId}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="truncate max-w-[200px]">{item.projectName}</span>
                    <span>{percentage.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted">
                    <div
                      className="h-2 rounded-full bg-primary transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
